import { clearMeasurementStorage } from './cookieConsent';

/**
 * החלקים חסרי-ה-framework של שער ההסכמה (HAT-1081): מתג ה-Consent Mode של
 * Google, ה-sweeper שמנקה את המכשיר כל עוד אין הסכמה, ודגל "הכלים כבר רצים"
 * שקובע אם חזרה מהסכמה באמצע ביקור מחייבת טעינה מחדש של העמוד.
 * ההפעלה עצמה של הכלים (PostHog / GA / Application Insights) נעשית
 * ב-ConsentProvider, שיש לו גישה ל-hooks שלהם.
 */

/** כל כמה זמן מנקים את המכשיר כשאין הסכמה. */
const SWEEP_INTERVAL_MS = 5000;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** האם כלי מדידה כלשהו הוזרק לעמוד הנוכחי - חזרה מהסכמה יודעת אז שעליה לפעול. */
let analyticsRunning = false;
let sweepTimer: ReturnType<typeof setInterval> | undefined;

/**
 * תור ה-gtag שמוגדר ב-index.html. משוחזר כאן רק למקרה החריג שהמסמך הוגש בלי
 * הסניפט, כדי שעדכון הסכמה לעולם לא ייפול בשקט.
 */
const gtag = (...args: unknown[]) => {
  if (!window.gtag) {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function () {
      // arguments ולא rest array: זו הצורה ש-gtag.js קורא בה פקודות.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag(...args);
};

/**
 * מתג ה-Consent Mode של Google: מתחיל "denied" לכל משתמש (ב-index.html) ומוזז
 * רק כאן, כך שגם תג של Google שהגיע לעמוד בדרך אחרת לא שומר דבר לפני הסכמה.
 */
export const setGoogleConsentState = (granted: boolean): void => {
  const state = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
};

/**
 * שומר על המכשיר נקי כל עוד אין הסכמה. ניקוי בודד לא מספיק: כלי שהוזרק מבחוץ
 * יכול להיטען בכל שלב של הביקור. הטיימר רץ גם בחזרה ללשונית, שאז סקריפט דחוי
 * נוטה להתעורר.
 */
export const startSweeping = (): void => {
  clearMeasurementStorage();
  if (sweepTimer !== undefined) return;

  sweepTimer = setInterval(clearMeasurementStorage, SWEEP_INTERVAL_MS);
  document.addEventListener('visibilitychange', clearMeasurementStorage);
};

export const stopSweeping = (): void => {
  if (sweepTimer === undefined) return;

  clearInterval(sweepTimer);
  sweepTimer = undefined;
  document.removeEventListener('visibilitychange', clearMeasurementStorage);
};

/** מסומן על ידי ConsentProvider אחרי שהכלים הופעלו בפועל בעמוד הזה. */
export const markAnalyticsRunning = (): void => {
  analyticsRunning = true;
};

/**
 * האם כלי מדידה באמת רץ בעמוד הזה. הבאנר שואל כדי להזהיר שחזרה מהסכמה תטען את
 * העמוד מחדש - ולשתוק כשזה לא יקרה.
 */
export const isAnalyticsRunning = (): boolean => analyticsRunning;

/**
 * חזרה מהסכמה אחרי שהכלים כבר רצים: סקריפט שכבר הוזרק אי אפשר לפרוק, וטעינה
 * מחדש היא מה שבאמת עוצר את האיסוף. מחזיר true אם העמוד עומד להיטען מחדש.
 */
export const reloadIfAnalyticsRunning = (): boolean => {
  if (!analyticsRunning) return false;

  analyticsRunning = false;
  window.location.reload();
  return true;
};
